import { Injectable, Injector } from '@angular/core';
import { BaseService } from '@shared/services/base.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from '@node_modules/rxjs/operators';
import * as moment from '@node_modules/moment';
import { PagedResultTotal } from '@shared/models/paged-result-total';
import { BackgroundJobExecutionDto, OrderListOutput } from '@shared/service-proxies/service-proxies';

@Injectable()
export class OrdersService extends BaseService {
    constructor(private readonly _httpClient: HttpClient, protected readonly injector: Injector) {
        super(injector);
    }

    /**
     * Loads a collection of Orders based on the given criteria
     */
    getOrders(
        orderNumber: string | undefined,
        billingAcountNumber: string | undefined,
        startDate: moment.Moment | undefined,
        endDate: moment.Moment | undefined,
        orderStatusTypeIds: string | undefined,
        statusStartDate: moment.Moment | undefined,
        statusEndDate: moment.Moment | undefined,
        contractStartDate: moment.Moment | undefined,
        contractEndDate: moment.Moment | undefined,
        partnerId: string | undefined,
        customerId: string | undefined,
        customerLocationId: string | undefined,
        providerId: string | undefined,
        productCategoryId: string | undefined,
        productId: string | undefined,
        spmIdList: string | undefined,
        investOwnersIdList: string | undefined,
        provisionerIdList: string | undefined,
        agentIdList: string | undefined,
        orderStatusIdList: string | undefined,
        contractTypeIdList: string | undefined,
        ignoreCache: boolean | undefined,
        advisorEntitlementId: number | undefined,
        salesCampaignIdList: string | undefined,
        internalSalesAgentIdList: string | undefined,
        accountManagerIdList: string | undefined,
        sorting: string | undefined,
        maxResultCount: number | undefined,
        skipCount: number | undefined
    ): Observable<PagedResultTotal<OrderListOutput>> {
        let url = this.environment.backendUrl + '/api/services/app/v2/orders';
        url = url.replace(/[?&]$/, '');

        let params = new HttpParams();

        params = params.appendAll({
            orderNumber: orderNumber,
            billingAcountNumber: billingAcountNumber,
            startDate: startDate != undefined ? encodeURIComponent(startDate.format('YYYY-MM-DD')) : '',
            endDate: endDate != undefined ? encodeURIComponent(endDate.format('YYYY-MM-DD')) : '',
            orderStatusTypeIds: orderStatusTypeIds,
            statusStartDate: statusStartDate != undefined ? encodeURIComponent(statusStartDate.format('YYYY-MM-DD')) : '',
            statusEndDate: statusEndDate != undefined ? encodeURIComponent(statusEndDate.format('YYYY-MM-DD')) : '',
            contractStartDate: contractStartDate != undefined ? encodeURIComponent(contractStartDate.format('YYYY-MM-DD')) : '',
            contractEndDate: contractEndDate != undefined ? encodeURIComponent(contractEndDate.format('YYYY-MM-DD')) : '',
            partnerId: partnerId,
            customerId: customerId,
            customerLocationId: customerLocationId,
            providerId: providerId,
            productCategoryId: productCategoryId,
            productId: productId,
            spmIdList: spmIdList,
            investOwnersIdList: investOwnersIdList,
            provisionerIdList: provisionerIdList,
            agentIdList: agentIdList,
            orderStatusIdList: orderStatusIdList,
            contractTypeIdList: contractTypeIdList,
            ignoreCache: ignoreCache,
            advisorEntitlementId: advisorEntitlementId ?? '',
            salesCampaignIdList: salesCampaignIdList,
            internalSalesAgentIdList: internalSalesAgentIdList,
            accountManagerIdList: accountManagerIdList,
            sorting: sorting,
            maxResultCount: maxResultCount,
            skipCount: skipCount,
        });

        return this._httpClient.get<any>(url, { params: params }).pipe(
            map((response: any) => {
                return (response.result as PagedResultTotal<OrderListOutput>) ?? null;
            }),
            catchError(this.handleError)
        );
    }

    enqueueOrdersSearchReport(
        orderNumber: string | undefined,
        billingAcountNumber: string | undefined,
        startDate: moment.Moment | undefined,
        endDate: moment.Moment | undefined,
        orderStatusTypeIds: string | undefined,
        statusStartDate: moment.Moment | undefined,
        statusEndDate: moment.Moment | undefined,
        contractStartDate: moment.Moment | undefined,
        contractEndDate: moment.Moment | undefined,
        partnerId: string | undefined,
        customerId: string | undefined,
        customerLocationId: string | undefined,
        providerId: string | undefined,
        productCategoryId: string | undefined,
        productId: string | undefined,
        spmIdList: string | undefined,
        investOwnersIdList: string | undefined,
        provisionerIdList: string | undefined,
        agentIdList: string | undefined,
        orderStatusIdList: string | undefined,
        contractTypeIdList: string | undefined,
        ignoreCache: boolean | undefined,
        advisorEntitlementId: number | undefined,
        salesCampaignIdList: string | undefined,
        internalSalesAgentIdList: string | undefined,
        accountManagerIdList: string | undefined,
        sorting: string | undefined,
        maxResultCount: number | undefined,
        skipCount: number | undefined
    ): Observable<BackgroundJobExecutionDto> {
        let url = this.environment.backendUrl + '/api/services/app/v2/orders/enqueue-orders-search-report';
        url = url.replace(/[?&]$/, '');

        const body = {
            orderNumber: orderNumber,
            billingAcountNumber: billingAcountNumber,
            startDate: startDate != undefined ? encodeURIComponent(startDate.format('YYYY-MM-DD')) : '',
            endDate: endDate != undefined ? encodeURIComponent(endDate.format('YYYY-MM-DD')) : '',
            orderStatusTypeIds: orderStatusTypeIds,
            statusStartDate: statusStartDate != undefined ? encodeURIComponent(statusStartDate.format('YYYY-MM-DD')) : '',
            statusEndDate: statusEndDate != undefined ? encodeURIComponent(statusEndDate.format('YYYY-MM-DD')) : '',
            contractStartDate: contractStartDate != undefined ? encodeURIComponent(contractStartDate.format('YYYY-MM-DD')) : '',
            contractEndDate: contractEndDate != undefined ? encodeURIComponent(contractEndDate.format('YYYY-MM-DD')) : '',
            partnerId: partnerId,
            customerId: customerId,
            customerLocationId: customerLocationId,
            providerId: providerId,
            productCategoryId: productCategoryId,
            productId: productId,
            spmIdList: spmIdList,
            investOwnersIdList: investOwnersIdList,
            provisionerIdList: provisionerIdList,
            agentIdList: agentIdList,
            orderStatusIdList: orderStatusIdList,
            contractTypeIdList: contractTypeIdList,
            ignoreCache: ignoreCache,
            advisorEntitlementId: advisorEntitlementId ?? '',
            salesCampaignIdList: salesCampaignIdList,
            internalSalesAgentIdList: internalSalesAgentIdList,
            accountManagerIdList: accountManagerIdList,
            sorting: sorting,
            maxResultCount: maxResultCount,
            skipCount: skipCount,
        };

        return this._httpClient.post<any>(url, body).pipe(
            map((response: any) => {
                return (response.result as BackgroundJobExecutionDto) ?? null;
            }),
            catchError(this.handleError)
        );
    }
}
